/**
 * Carrom Canvas Component
 *
 * HTML5 Canvas rendering for the Carrom board game.
 * Draws: board, pockets, coins, striker, aim line, power bar.
 * Handles: mouse/touch events for striker positioning and slingshot aiming.
 * Runs requestAnimationFrame loop for smooth physics rendering.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  CarromPlayer,
  CoinState,
  ShotInput,
  AimState,
  PhysicsBody,
  BOARD_SIZE,
  BORDER_WIDTH,
  CENTER,
  POCKET_POSITIONS,
  POCKET_RADIUS,
  COIN_RADIUS,
  STRIKER_RADIUS,
  BASELINE_OFFSET,
  MAX_POWER,
} from '../types';
import { CarromPhysics } from '../CarromPhysics';
import { PowderType } from '../types';
import './CarromCanvas.css';

interface CarromCanvasProps {
  physics: CarromPhysics;
  coins: CoinState[];
  currentPlayer: CarromPlayer;
  isSimulating: boolean;
  isPlayerTurn: boolean;
  onShot: (shot: ShotInput) => void;
  gameOver: boolean;
  powderType?: PowderType;
  powderAnimating?: boolean;
}

const CarromCanvas: React.FC<CarromCanvasProps> = ({
  physics,
  coins,
  currentPlayer,
  isSimulating,
  isPlayerTurn,
  onShot,
  gameOver,
  powderType = 'light',
  powderAnimating = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const powderParticlesRef = useRef<Array<{ x: number; y: number; alpha: number; size: number; vy: number }>>([]); 

  const [aimState, setAimState] = useState<AimState>({
    isAiming: false,
    isDraggingStriker: false,
    strikerX: CENTER,
    angle: 0,
    power: 0,
    dragStartX: 0,
    dragStartY: 0,
  });

  const aimStateRef = useRef(aimState);
  aimStateRef.current = aimState;

  const baselineY = currentPlayer === 'player1'
    ? BOARD_SIZE - BORDER_WIDTH - BASELINE_OFFSET
    : BORDER_WIDTH + BASELINE_OFFSET;

  const strikerXMin = BORDER_WIDTH + STRIKER_RADIUS + 40;
  const strikerXMax = BOARD_SIZE - BORDER_WIDTH - STRIKER_RADIUS - 40;

  // =========================================================================
  // Canvas Coordinate Conversion
  // =========================================================================

  const getCanvasCoords = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      const scaleX = BOARD_SIZE / rect.width;
      const scaleY = BOARD_SIZE / rect.height;

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    },
    []
  );

  // =========================================================================
  // Drawing
  // =========================================================================

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, BOARD_SIZE, BOARD_SIZE);

    // Draw board background
    drawBoard(ctx);

    // Draw powder overlay on the playing surface
    drawPowderOverlay(ctx);

    // Draw pockets
    drawPockets(ctx);

    // Draw powder particles (animation)
    if (powderAnimating) {
      drawPowderParticles(ctx);
    }

    // Draw coins from physics bodies
    const bodies = physics.getBodies();
    for (const body of bodies) {
      if (!body.pocketed && body.type !== 'striker') {
        drawCoin(ctx, body);
      }
    }

    // Draw striker
    if (isSimulating) {
      const striker = physics.getBody('striker');
      if (striker && !striker.pocketed) {
        drawStriker(ctx, striker.x, striker.y);
      }
    } else if (!gameOver && isPlayerTurn) {
      // Draw striker on baseline
      drawStriker(ctx, aimStateRef.current.strikerX, baselineY);

      // Draw aim line if aiming
      if (aimStateRef.current.isAiming) {
        drawAimLine(ctx, aimStateRef.current);
        drawPowerBar(ctx, aimStateRef.current.power);
      }
    }
  }, [physics, isSimulating, isPlayerTurn, gameOver, baselineY, powderType, powderAnimating]);

  /** Draw a subtle powder texture overlay on the board surface */
  const drawPowderOverlay = (ctx: CanvasRenderingContext2D): void => {
    if (powderType === 'none') return;

    const playX = BORDER_WIDTH;
    const playY = BORDER_WIDTH;
    const playSize = BOARD_SIZE - BORDER_WIDTH * 2;

    // Powder opacity increases with amount
    const opacityMap: Record<string, number> = { light: 0.04, medium: 0.08, heavy: 0.14 };
    const opacity = opacityMap[powderType] || 0;

    ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
    ctx.fillRect(playX, playY, playSize, playSize);

    // Subtle speckle pattern for texture
    const speckleCount = powderType === 'light' ? 40 : powderType === 'medium' ? 100 : 200;
    ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 2})`;
    // Use deterministic positions based on board size (not random per frame)
    for (let i = 0; i < speckleCount; i++) {
      const px = playX + ((i * 137.5) % playSize);
      const py = playY + ((i * 97.3 + i * i * 0.7) % playSize);
      const size = 1 + (i % 3);
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  /** Draw falling powder particles during the powder change animation */
  const drawPowderParticles = (ctx: CanvasRenderingContext2D): void => {
    const particles = powderParticlesRef.current;

    // Spawn new particles
    if (particles.length < 60) {
      for (let i = 0; i < 5; i++) {
        particles.push({
          x: BORDER_WIDTH + Math.random() * (BOARD_SIZE - BORDER_WIDTH * 2),
          y: BORDER_WIDTH + Math.random() * 50,
          alpha: 0.6 + Math.random() * 0.4,
          size: 1 + Math.random() * 2.5,
          vy: 1 + Math.random() * 3,
        });
      }
    }

    // Draw and update particles
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${p.alpha})`;
      ctx.fill();

      // Update
      p.y += p.vy;
      p.alpha -= 0.008;

      // Remove dead particles
      if (p.alpha <= 0 || p.y > BOARD_SIZE - BORDER_WIDTH) {
        particles.splice(i, 1);
      }
    }
  };

  const drawBoard = (ctx: CanvasRenderingContext2D): void => {
    // Outer border
    ctx.fillStyle = '#5D4037';
    ctx.fillRect(0, 0, BOARD_SIZE, BOARD_SIZE);

    // Playing surface
    const playX = BORDER_WIDTH;
    const playY = BORDER_WIDTH;
    const playSize = BOARD_SIZE - BORDER_WIDTH * 2;

    ctx.fillStyle = '#F5DEB3';
    ctx.fillRect(playX, playY, playSize, playSize);

    // Inner border lines
    ctx.strokeStyle = '#8B4513';
    ctx.lineWidth = 2;
    const innerMargin = 30;
    ctx.strokeRect(
      playX + innerMargin,
      playY + innerMargin,
      playSize - innerMargin * 2,
      playSize - innerMargin * 2
    );

    // Center circle
    ctx.beginPath();
    ctx.arc(CENTER, CENTER, 40, 0, Math.PI * 2);
    ctx.strokeStyle = '#8B4513';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Inner center circle
    ctx.beginPath();
    ctx.arc(CENTER, CENTER, 20, 0, Math.PI * 2);
    ctx.stroke();

    // Baseline indicators for both players
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = 'rgba(139, 69, 19, 0.3)';
    ctx.lineWidth = 1;

    // Player 1 baseline (bottom)
    const p1BaseY = BOARD_SIZE - BORDER_WIDTH - BASELINE_OFFSET;
    ctx.beginPath();
    ctx.moveTo(strikerXMin - 10, p1BaseY);
    ctx.lineTo(strikerXMax + 10, p1BaseY);
    ctx.stroke();

    // Player 2 baseline (top)
    const p2BaseY = BORDER_WIDTH + BASELINE_OFFSET;
    ctx.beginPath();
    ctx.moveTo(strikerXMin - 10, p2BaseY);
    ctx.lineTo(strikerXMax + 10, p2BaseY);
    ctx.stroke();

    ctx.setLineDash([]);

    // Diagonal lines in corners (decorative)
    const cornerLen = 25;
    ctx.strokeStyle = '#8B4513';
    ctx.lineWidth = 1.5;

    const corners = [
      { x: playX + innerMargin, y: playY + innerMargin },
      { x: playX + playSize - innerMargin, y: playY + innerMargin },
      { x: playX + innerMargin, y: playY + playSize - innerMargin },
      { x: playX + playSize - innerMargin, y: playY + playSize - innerMargin },
    ];

    for (const corner of corners) {
      const dirX = corner.x < CENTER ? 1 : -1;
      const dirY = corner.y < CENTER ? 1 : -1;

      ctx.beginPath();
      ctx.moveTo(corner.x, corner.y);
      ctx.lineTo(corner.x + dirX * cornerLen, corner.y + dirY * cornerLen);
      ctx.stroke();
    }
  };

  const drawPockets = (ctx: CanvasRenderingContext2D): void => {
    for (const pocket of POCKET_POSITIONS) {
      ctx.beginPath();
      ctx.arc(pocket.x, pocket.y, POCKET_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = '#2E1B0E';
      ctx.fill();

      // Pocket rim
      ctx.beginPath();
      ctx.arc(pocket.x, pocket.y, POCKET_RADIUS + 2, 0, Math.PI * 2);
      ctx.strokeStyle = '#4E342E';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  };

  const drawCoin = (ctx: CanvasRenderingContext2D, body: PhysicsBody): void => {
    ctx.beginPath();
    ctx.arc(body.x, body.y, body.radius, 0, Math.PI * 2);

    switch (body.type) {
      case 'white':
        ctx.fillStyle = '#FAFAFA';
        ctx.fill();
        ctx.strokeStyle = '#BDBDBD';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      case 'black':
        ctx.fillStyle = '#212121';
        ctx.fill();
        ctx.strokeStyle = '#424242';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        break;
      case 'queen':
        ctx.fillStyle = '#E53935';
        ctx.fill();
        ctx.strokeStyle = '#B71C1C';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Crown symbol
        ctx.fillStyle = '#FFEB3B';
        ctx.font = `${body.radius}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('♛', body.x, body.y + 1);
        break;
    }

    // Subtle inner circle for depth
    if (body.type !== 'queen') {
      ctx.beginPath();
      ctx.arc(body.x, body.y, body.radius * 0.6, 0, Math.PI * 2);
      ctx.strokeStyle = body.type === 'white' ? '#E0E0E0' : '#333';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
  };

  const drawStriker = (ctx: CanvasRenderingContext2D, x: number, y: number): void => {
    // Outer ring
    ctx.beginPath();
    ctx.arc(x, y, STRIKER_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = '#FFF9C4';
    ctx.fill();

    const borderColor = currentPlayer === 'player1' ? '#1565C0' : '#C62828';
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 3;
    ctx.stroke();

    // Inner circle
    ctx.beginPath();
    ctx.arc(x, y, STRIKER_RADIUS * 0.5, 0, Math.PI * 2);
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  };

  const drawAimLine = (ctx: CanvasRenderingContext2D, aim: AimState): void => {
    const startX = aim.strikerX;
    const startY = baselineY;
    const lineLength = 150 + aim.power * 200;

    const endX = startX + Math.cos(aim.angle) * lineLength;
    const endY = startY + Math.sin(aim.angle) * lineLength;

    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.5 + aim.power * 0.5})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Arrow head
    const arrowSize = 8;
    const arrowAngle = aim.angle;
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowSize * Math.cos(arrowAngle - 0.4),
      endY - arrowSize * Math.sin(arrowAngle - 0.4)
    );
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowSize * Math.cos(arrowAngle + 0.4),
      endY - arrowSize * Math.sin(arrowAngle + 0.4)
    );
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const drawPowerBar = (ctx: CanvasRenderingContext2D, power: number): void => {
    const barWidth = 120;
    const barHeight = 12;
    const barX = BOARD_SIZE / 2 - barWidth / 2;
    const barY = BOARD_SIZE - 8;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(barX - 2, barY - 2, barWidth + 4, barHeight + 4);

    // Fill
    const fillWidth = barWidth * power;
    const gradient = ctx.createLinearGradient(barX, barY, barX + barWidth, barY);
    gradient.addColorStop(0, '#4CAF50');
    gradient.addColorStop(0.5, '#FFC107');
    gradient.addColorStop(1, '#F44336');
    ctx.fillStyle = gradient;
    ctx.fillRect(barX, barY, fillWidth, barHeight);

    // Border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.strokeRect(barX, barY, barWidth, barHeight);

    // Label
    ctx.fillStyle = '#fff';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`Power: ${Math.round(power * 100)}%`, BOARD_SIZE / 2, barY - 4);
  };

  // =========================================================================
  // Animation Loop
  // =========================================================================

  useEffect(() => {
    lastTimeRef.current = 0;

    const animate = (time: number) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = time;
      }

      // Cap dt to prevent spiral of death after tab switch
      const dt = Math.min((time - lastTimeRef.current) / 1000, 1 / 20);
      lastTimeRef.current = time;

      if (isSimulating) {
        physics.step(dt);
      }

      draw();
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      lastTimeRef.current = 0;
    };
  }, [physics, isSimulating, draw]);

  // =========================================================================
  // Input Handling
  // =========================================================================

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (isSimulating || !isPlayerTurn || gameOver) return;

      const { x, y } = getCanvasCoords(e.clientX, e.clientY);

      // Anywhere on or near the baseline starts a drag/aim interaction.
      // We always record the start position; pointerMove decides whether
      // the user is repositioning (horizontal drag) or aiming (vertical pull).
      const baselineRange = 50;
      if (Math.abs(y - baselineY) < baselineRange) {
        setAimState((prev) => ({
          ...prev,
          isDraggingStriker: true,
          isAiming: false,
          dragStartX: x,
          dragStartY: y,
          power: 0,
          // Snap striker to click X immediately
          strikerX: Math.max(strikerXMin, Math.min(strikerXMax, x)),
        }));
      }

      e.preventDefault();
    },
    [isSimulating, isPlayerTurn, gameOver, getCanvasCoords, baselineY, strikerXMin, strikerXMax]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (isSimulating || !isPlayerTurn || gameOver) return;

      const { x, y } = getCanvasCoords(e.clientX, e.clientY);
      const aim = aimStateRef.current;

      if (!aim.isDraggingStriker && !aim.isAiming) return;

      // How far has the pointer moved from the initial press?
      const verticalDist = Math.abs(y - aim.dragStartY);
      const horizontalDist = Math.abs(x - aim.dragStartX);

      // If the user has pulled away vertically (> 15px), switch to aiming mode
      if (aim.isDraggingStriker && verticalDist > 15) {
        setAimState((prev) => ({
          ...prev,
          isDraggingStriker: false,
          isAiming: true,
        }));
      }

      if (aim.isDraggingStriker || (aim.isAiming && verticalDist <= 15 && horizontalDist > verticalDist)) {
        // Repositioning: move striker along baseline
        const clampedX = Math.max(strikerXMin, Math.min(strikerXMax, x));
        setAimState((prev) => ({
          ...prev,
          strikerX: clampedX,
        }));
      }

      if (aimStateRef.current.isAiming) {
        // Slingshot aiming: drag backward, shoot forward
        const dragDx = aimStateRef.current.strikerX - x;
        const dragDy = baselineY - y;
        const dragDist = Math.sqrt(dragDx * dragDx + dragDy * dragDy);

        // Direction is opposite of drag (slingshot)
        const angle = Math.atan2(dragDy, dragDx);

        // Power based on drag distance
        const maxDragDist = 80;
        const rawPower = dragDist / maxDragDist;
        const power = Math.min(MAX_POWER, rawPower * rawPower * 0.6 + rawPower * 0.4);

        setAimState((prev) => ({
          ...prev,
          angle,
          power,
        }));
      }

      e.preventDefault();
    },
    [isSimulating, isPlayerTurn, gameOver, getCanvasCoords, strikerXMin, strikerXMax, baselineY]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const aim = aimStateRef.current;

      if (aim.isDraggingStriker) {
        setAimState((prev) => ({
          ...prev,
          isDraggingStriker: false,
        }));
      } else if (aim.isAiming && aim.power > 0.05) {
        // Fire the shot
        onShot({
          strikerX: aim.strikerX,
          angle: aim.angle,
          power: aim.power,
        });

        setAimState((prev) => ({
          ...prev,
          isAiming: false,
          power: 0,
        }));
      } else {
        setAimState((prev) => ({
          ...prev,
          isAiming: false,
          power: 0,
        }));
      }

      e.preventDefault();
    },
    [onShot]
  );

  // Reset striker position when player changes
  useEffect(() => {
    setAimState((prev) => ({
      ...prev,
      strikerX: CENTER,
      isAiming: false,
      isDraggingStriker: false,
      power: 0,
    }));
  }, [currentPlayer]);

  return (
    <div className="carrom-canvas-container" ref={containerRef}>
      <canvas
        ref={canvasRef}
        width={BOARD_SIZE}
        height={BOARD_SIZE}
        className="carrom-canvas"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{ touchAction: 'none' }}
        aria-label="Carrom board"
        role="img"
      />
      {!isSimulating && isPlayerTurn && !gameOver && (
        <div className="carrom-canvas__hint" aria-live="polite">
          Click baseline to position striker. Pull away to aim and release to shoot.
        </div>
      )}
    </div>
  );
};

export { CarromCanvas };
export default CarromCanvas;
